import { getIn } from "formik";
import {
  FGSelectInput,
  IFGSelectInputProps,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import { ReferentialItemDto } from "../../api";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import { nameof } from "../../utils";

export interface ICitiesSelectProps
  extends Omit<
    IFGSelectInputProps<any>,
    "items" | "loading" | "displayField" | "valueField"
  > {
  countryFieldName?: string;
  // When provided, keeps this (hidden) field in sync with the selected city's postal code.
  codePostalTextName?: string;
}

export const CitiesSelect: React.FunctionComponent<ICitiesSelectProps> = ({
  countryFieldName,
  codePostalTextName,
  ...fgSelectInputProps
}) => {
  const { t, apis } = useCommonHooks();
  const { formik } = useFGContext();

  const countryCode = React.useMemo(
    () => (!!countryFieldName ? getIn(formik?.values, countryFieldName) : null),
    [countryFieldName, formik?.values]
  );

  const { data, isFetching } = useQuery(
    ["ref-cities", countryCode],
    async () => {
      const cities = await apis.referentials.cities(countryCode);
      return cities.map((r) => ({ ...r, idValue: +r.idValue }));
    }
  );

  const fieldName = (fgSelectInputProps as any).name as string;
  const selectedIdCodePostal = React.useMemo(
    () => (fieldName ? getIn(formik?.values, fieldName) : null),
    [fieldName, formik?.values]
  );

  React.useEffect(() => {
    if (!codePostalTextName || !data?.length) return;
    const selected = selectedIdCodePostal
      ? data.find((c) => +c.idValue === +selectedIdCodePostal)
      : null;
    const codePostal = selected?.keyValue ?? null;
    const currentCpText = getIn(formik?.values, codePostalTextName);
    // Don't create a spurious address object when there's nothing selected
    // and nothing to clear (e.g. an empty Courrier/Autre address block).
    if (
      !codePostal &&
      (currentCpText === undefined ||
        currentCpText === null ||
        currentCpText === "")
    )
      return;
    if (currentCpText !== codePostal) {
      formik.setFieldValue(codePostalTextName, codePostal);
    }
  }, [codePostalTextName, data, selectedIdCodePostal, formik]);

  return (
    <FGSelectInput
      label={fgSelectInputProps.label || t(ETLCodes.Localites)}
      items={data}
      loading={isFetching}
      displayField={nameof<ReferentialItemDto>("displayValue")}
      valueField={nameof<ReferentialItemDto>("idValue")}
      {...fgSelectInputProps}
    />
  );
};
